import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useGet, usePost, usePut } from "@/apiHooks/HttpFunctions";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import LocalizedInput from "@/components/LocalizedInput";
import { ProductCategoryInitialValues } from "@/data/WebDataManagement/MasterData/ProductCategories/initial-values";
import ProductCategorySchema from "@/schema/WebDataManagement/MasterData/ProductCategories/ProductCategorySchema";
import RenderToast from "@/components/RenderToast";
import { productCategoriesMasterDataApi } from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { useSelector } from "react-redux";
import StatusInput from "@/components/StatusInput";

const ProductCategoryModal = forwardRef((_, ref) => {
  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );

  const localeCodes = useMemo(() => {
    return locales ? locales.map((locale) => locale.code) : [];
  }, [locales]);

  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${productCategoriesMasterDataApi}/${modalState.id}`
    : null;

  const {
    data: showData,
    isSuccess: showIsSuccess,
    error: showError,
  } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Material ${action} successfully.`,
    });
  };
  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${productCategoriesMasterDataApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: productCategoriesMasterDataApi,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ProductCategoryInitialValues(
      localeCodes,
      modalState.id ? showData : null
    ),
    validationSchema: ProductCategorySchema(localeCodes, !!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const payload = {
      code: values.code,
      status: values.status,
      localizations: {},
    };
    Object.keys(values.localizations).forEach((key) => {
      if (values.localizations[key]) {
        payload.localizations[key] = values.localizations[key];
      }
    });
    modalState.id
      ? saveEdit(JSON.stringify(payload))
      : saveCreate(JSON.stringify(payload));
  };

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [formik, resetError]);

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Product Category" : "Add Product Category"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"code"}
            saveError={saveError}
            label="ID"
          />
        </Col>
      </Row>

      <LocalizedInput
        formik={formik}
        error={saveError}
        isEdit={!!modalState.id}
      />

      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default ProductCategoryModal;
